import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Button
} from '@mui/material';
import {
  Inbox,
  Send,
  Drafts,
  DeleteOutline,
  ReportProblemOutlined,
  OpenInNew,
  EmailOutlined
} from '@mui/icons-material';
import { MailFolder, MailFolderKey } from '../types';
import { MAIL_FOLDERS, MAIL_COLORS, CARBONIO_WEB_URL } from '../constants';

interface MailFolderListProps {
  currentFolder: MailFolderKey;
  folders: MailFolder[];
  userEmail?: string;
  onSelectFolder: (folder: MailFolderKey) => void;
}

export const MailFolderList: React.FC<MailFolderListProps> = ({
  currentFolder,
  folders,
  userEmail,
  onSelectFolder
}) => {
  const getFolderIcon = (key: MailFolderKey) => {
    switch (key) {
      case 'inbox':
        return <Inbox sx={{ fontSize: 20 }} />;
      case 'sent':
        return <Send sx={{ fontSize: 20 }} />;
      case 'drafts':
        return <Drafts sx={{ fontSize: 20 }} />;
      case 'trash':
        return <DeleteOutline sx={{ fontSize: 20 }} />;
      case 'junk':
        return <ReportProblemOutlined sx={{ fontSize: 20 }} />;
      default:
        return <Inbox sx={{ fontSize: 20 }} />;
    }
  };

  const getUnreadCount = (folderKey: MailFolderKey): number => {
    const match = folders.find(
      f => f.name.toLowerCase() === folderKey || f.path.toLowerCase().includes(folderKey)
    );
    return match?.unreadCount || 0;
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 240 },
        flexShrink: 0,
        bgcolor: '#ffffff',
        borderRight: `1px solid ${MAIL_COLORS.borderLight}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: 2,
        height: '100%'
      }}
    >
      <Box>
        {/* Список папок */}
        <Typography
          variant="caption"
          sx={{
            px: 1,
            mb: 1,
            display: 'block',
            fontWeight: 700,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.7rem'
          }}
        >
          Папки
        </Typography>

        <List component="nav" disablePadding>
          {MAIL_FOLDERS.map((item) => {
            const isSelected = currentFolder === item.key;
            const unreadCount = getUnreadCount(item.key);

            return (
              <ListItemButton
                key={item.key}
                selected={isSelected}
                onClick={() => onSelectFolder(item.key)}
                sx={{
                  borderRadius: '10px',
                  mb: 0.5,
                  py: 1,
                  px: 1.5,
                  color: isSelected ? MAIL_COLORS.primary : '#334155',
                  bgcolor: isSelected ? '#eff8ff' : 'transparent',
                  fontWeight: isSelected ? 700 : 500,
                  '&:hover': {
                    bgcolor: isSelected ? '#eff8ff' : '#f8fafc',
                    color: isSelected ? MAIL_COLORS.primary : '#0f172a'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isSelected ? MAIL_COLORS.primary : '#64748b',
                    minWidth: 32
                  }}
                >
                  {getFolderIcon(item.key)}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.88rem',
                    fontWeight: isSelected ? 700 : 500
                  }}
                />
                {unreadCount > 0 && (
                  <Chip
                    label={unreadCount > 999 ? '999+' : unreadCount}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      bgcolor: isSelected ? MAIL_COLORS.primary : '#e2e8f0',
                      color: isSelected ? '#ffffff' : '#334155',
                      borderRadius: '10px'
                    }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Нижний блок: Учетная запись и ссылка на Carbonio Webmail */}
      <Box sx={{ pt: 2, borderTop: `1px solid ${MAIL_COLORS.borderLight}`, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Email / Логин текущего пользователя */}
        {userEmail && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: '#f8fafc',
              border: `1px solid ${MAIL_COLORS.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <EmailOutlined sx={{ color: MAIL_COLORS.primary, fontSize: 18 }} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: 'block',
                  letterSpacing: '0.5px'
                }}
              >
                Учетная запись
              </Typography>
              <Typography
                variant="body2"
                title={userEmail}
                sx={{
                  fontWeight: 600,
                  color: MAIL_COLORS.textPrimary,
                  fontSize: '0.82rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {userEmail}
              </Typography>
            </Box>
          </Box>
        )}
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={() => window.open(CARBONIO_WEB_URL, '_blank', 'noopener,noreferrer')}
          endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '0.82rem',
            color: '#64748b',
            borderColor: '#e2e8f0',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#f8fafc',
              borderColor: '#cbd5e1',
              color: '#1e293b'
            }
          }}
        >
          Carbonio Webmail
        </Button>
      </Box>
    </Box>
  );
};

export default MailFolderList;
